import { useState } from "react";
import type { FormEvent } from "react";
import { Logo } from "@/components/Logo";
import { ApiError, AuthError } from "@/api/client";
import "./Login.css";

interface LoginProps {
  onSubmit: (email: string, password: string) => Promise<void>;
}

/**
 * The gate in front of every screen except `/entry` (feature/auth). Nothing behind
 * this form renders until `onSubmit` resolves — see App.tsx.
 *
 * Honest failure text (task requirement): the SAME generic message is shown whether
 * the email does not exist or the password is wrong, matching what the server
 * itself does (AuthEndpoints.cs) — this form has no way to say more even if it
 * wanted to, because the API never tells it which one happened.
 */
export function Login({ onSubmit }: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage(null);
    try {
      await onSubmit(email, password);
      // On success the parent re-renders the authenticated shell; this component
      // unmounts, so there is no further local state to set.
    } catch (err) {
      setStatus("error");
      /*
        "CHECK YOUR CONNECTION" IS A CLAIM ABOUT THE OPERATOR'S NETWORK, and for a 5xx it is
        a false one.

        MEASURED 2026-09-07 by stopping postgres and signing in: login returns **500**, which
        is not an AuthError, so it fell through to the generic line and told Christy to check
        her connection. Her connection is fine — the database is down. At a booth running on
        a cellular hotspot that sends her to fiddle with the hotspot, in front of a prospect,
        while the actual fault is somewhere she cannot reach.

        It is the third time today the same habit has surfaced: a scan taken before reference
        data loaded was reported as an unreadable receipt, and a 500 from the analyzer was
        reported as an unreadable receipt. Blaming whatever the person is holding — their
        paper, their phone, their wifi — for the app's own state.

        `AuthError` is unchanged and still first: "Incorrect email or password" is the server
        ANSWERING, and rewording it would leak which half was wrong. `neverReachedTheServer`
        (0 / 502 / 504) keeps the connection wording, because there the request genuinely did
        not arrive. A 5xx sits between them and now says so.
      */
      setErrorMessage(
        err instanceof AuthError
          ? err.message
          : err instanceof ApiError && err.status >= 500 && !err.neverReachedTheServer
            ? "EasyFarm couldn't sign you in just now — that is on our side, not your "
              + "connection. Try again in a moment."
            : "Could not sign in — check your connection and try again.",
      );
    }
  }

  return (
    <div className="login">
      <main className="login__card">
        {/*
          The wordmark sits on its own deep-green band, not on the card's white
          surface. Logo's own docs are explicit about why: the gold accent is
          7.94:1 on deep green and 1.76:1 on white, "the entire reason the header
          bar went dark." Rendering `onDark` (which lightens "Easy") straight onto
          the white card measured 1.07:1 for "Easy" and 1.76:1 for "Farm" — the
          brand was, in practice, invisible on the first screen of the demo. The
          band restores the same dark-masthead-over-light-content shape the shell
          and the Dashboard already use, rather than adding a contrast exception.
        */}
        <div className="login__brand">
          <Logo size={40} showTagline onDark />
        </div>

        <form className="login__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <h1 className="login__title">Sign in</h1>

          <div className="login__field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="login__field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {status === "error" && errorMessage && (
            <p className="login__error" role="alert">
              {errorMessage}
            </p>
          )}

          <button type="submit" className="btn btn--primary" disabled={status === "submitting"}>
            {status === "submitting" ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {/*
          THE ONE RECOVERY FROM A MISTAKE THAT CANNOT BE UNPRINTED.

          The posterboard QR must encode `/entry`, not the bare host —
          `docs/demo/tradeshow-2026-09.md` says so in bold, and calls it out as a PRINTED
          asset: "getting it wrong cannot be fixed on 2026-09-23." Everything else about the
          sign pushes toward the shortest possible payload, and the shortest possible payload
          is the bare host.

          MEASURED 2026-09-07 on a cookie-less phone context, before writing this: the bare
          host serves this form with **zero links and one button**. A visitor who scans a
          mis-printed code is standing at a password prompt with no route to the demo, and
          the only remedy on the day is a person noticing and saying so.

          This line does not make the QR correct. It makes getting it wrong SURVIVABLE, which
          is the most a software change can do about a sign that is already at the printer.
          It is unconditional because /entry is public on both surfaces by design — the QR
          visitor "has no account and never will" (DemoEndpoints) — so it exposes nothing a
          scan of the correct code would not.
        */}
        <p className="login__visitor">
          Scanned the code at our booth?{" "}
          <a className="login__visitor-link" href="/entry">
            Add an entry without signing in
          </a>
        </p>
      </main>
    </div>
  );
}
